import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { Box } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import {
    Fragment,
    KeyboardEvent as ReactKeyboardEvent,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react';
import { useIntl } from 'react-intl';
import { Panel, PanelGroup } from 'react-resizable-panels';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { isTextEntryElement } from '~/utils/textEntry';
import { isExcludedFromViewInput } from '~/utils/viewInputExclusion';
import {
    BlendAdapter,
    DatasetAdapter,
    EnvironmentAdapter,
    ImageDetailsContext,
    RowOverlayContext,
    PrimaryImageControls,
    RenderableVolume,
    RenderAdapter,
    TileAlignment,
    TileGeometryLink,
    TileOverlayContext,
    TileStatusContext,
    WindowingAdapter,
} from '../adapters/types';
import { useChangeVersion } from '../adapters/useChangeVersion';
import { CHROME_BG, LEFT_EDGE_PANE_STYLE, READOUT_FONT } from '../chrome';
import { chromeTheme } from '../chromeTheme';
import { DatasetBinding } from '../binding/useDatasetBinding';
import {
    isPanelCell,
    locateViewport,
    neighbourViewport,
    panelKindOfCell,
    rowIndexOfSeries,
} from '../layout/layout';
import { setRowHeights } from '../layout/mutations';
import {
    ImageLayer,
    Layout,
    PanelKind,
    Plane,
    PRIMARY_ROW_INDEX,
    RowFusion,
    RowLock,
    RowTemplate,
    ViewportId,
} from '../layout/types';
import { nextPlaneOfPane, PlaneCycleControl } from '../layout/planeCycle';
import { WindowLevelFanout } from '../linking/useWindowLevelFanout';
import {
    RowLockState,
    lockThrough,
    rowLockStateOf,
    tileAlignmentOf,
    tileGeometryLinkOf,
    tileStructureSourceOf,
} from '../linking/rowLock';
import PanelTile from '../tile/PanelTile';
import Tile from '../tile/Tile';
import {
    resolvePaneHistograms,
    resolveOwnRowBlend,
    resolveRowBlend,
    RowBlendControls,
    tileFusionOf,
    resolveRowFrame,
    RowFrameControls,
} from '../tile/tileLink';
import {
    planeLabel,
    roiSelectionFor,
    RoiSelectionHandler,
    structureActionsFor,
    TileStructureActions,
    imagesDrawnByPane,
    seriesNumberText,
    fusedImageNamedByPane,
    ListedImage,
    shownImageOfPane,
} from '../tile/tileViewModel';
import SecondaryImagePlaceholder from '../tile/SecondaryImagePlaceholder';
import RowWidgetLayer from '../widget/RowWidgetLayer';
import { WidgetFrame } from '../widget/widgetFrame';
import GridRow from './GridRow';
import GridRowFooter from './GridRowFooter';
import { GridPanel } from './GridPanel';
import { GridWidget } from './GridWidget';
import { imagesLedByPane } from './imagesLedByPane';
import PrimaryRowNotice from './PrimaryRowNotice';
import ResizeGutter from './ResizeGutter';
import { RowHost } from './RowHost';

interface ViewportGridProps {
    layout: Layout;
    datasetAdapter: DatasetAdapter;
    environmentAdapter: EnvironmentAdapter;
    /** Per-series window/level truth (the engine bus, or a test double). */
    windowingAdapter: WindowingAdapter;
    /** Per-row secondary-image blend truth. */
    blendAdapter: BlendAdapter;
    activeId: ViewportId | undefined;
    /**
     * Whether the activated pane is the pane of an unbound row, which draws
     * no image. False while a tile is active or nothing is.
     */
    unboundRowActive?: boolean;
    maximizedId: ViewportId | undefined;
    /** Whether the image tiles draw their scale bar. */
    scaleBarVisible?: boolean;
    /**
     * Host-supplied per-tile canvas overlay (the engine overlays of a tile).
     * Receives the tile's renderer.
     */
    tileOverlay?: (context: TileOverlayContext) => ReactNode;
    /**
     * Host-supplied status of the images each image tile draws, shown in
     * its title bar after the slice readout.
     */
    tileStatus?: (context: TileStatusContext) => ReactNode;
    /** Host-supplied details of the image a tile names, hung on its name. */
    imageDetails?: (context: ImageDetailsContext) => ReactNode;
    /**
     * Host-supplied controls of what the host draws over a row's image,
     * shown at the far end of the row's footer, before the row's shape.
     */
    rowOverlayControls?: (context: RowOverlayContext) => ReactNode;
    /** The panels the layout's panel panes show, one per kind. */
    panels?: ReadonlyArray<GridPanel>;
    /** Floated over their rows, in the order they open in. */
    widgets?: ReadonlyArray<GridWidget>;
    /**
     * The active structure set's owning dataset series. Tiles of this series
     * form the editable set: the tiles a stroke may write into.
     */
    editableSeriesUid: SeriesInstanceUid | undefined;
    /**
     * What the primary row tells the user when it does not show the primary
     * image that the sidebar names; `undefined` when it does.
     */
    primaryRowNotice?: string;
    /** How the primary row changes the primary image, and what else it offers on it. */
    primaryImage: PrimaryImageControls;
    onActivate: (id: ViewportId) => void;
    /** Activates the pane of the unbound row at `rowIndex`. */
    onActivateUnboundRow: (rowIndex: number) => void;
    /**
     * Fires on each activation change with the active tile's renderer and
     * whether a pane is activated at all: (renderer, true) for a pane that
     * draws an image, (undefined, true) for an activated pane that draws
     * none (a panel, a secondary pane of a row with no secondary image, or
     * the pane of an unbound row), and (undefined, false) when nothing is
     * activated or the grid unmounts.
     * Must be a stable callback.
     */
    onActiveRendererChanged?: (
        renderer: RenderAdapter | undefined,
        paneActivated: boolean
    ) => void;
    /** Whether a tile click should pick the ROI under it (pan mode only). */
    canSelectRoi?: boolean;
    /** Selects the ROI a click resolves on an editable tile. */
    onSelectRoi?: RoiSelectionHandler;
    /** What an editable tile's hover label offers on the structure under it. */
    structureActions?: TileStructureActions;
    onToggleMaximize: (id: ViewportId) => void;
    /** Opens an unbound row directly below the given row. */
    onAddRowBelow: (rowIndex: number) => void;
    onCloseRow: (rowIndex: number) => void;
    /** Reorder a viewport within its row to a new column index (drag). */
    onReorder: (id: ViewportId, toColumnIndex: number) => void;
    /** Swaps a row between the blend and its fused pair shown apart. */
    onToggleSideBySide: (rowIndex: number) => void;
    /** Cycles a row's panes one step through the planes they show. */
    onRotateRowPlanes: (rowIndex: number) => void;
    /** Hangs one row in the given shape, its image and pair kept. */
    onRehangRow: (rowIndex: number, template: RowTemplate) => void;
    /** Locks a row to the primary row, or frees it. */
    onSetRowLock: (rowIndex: number, lock: RowLock) => void;
    /** Sets or drops the own fusion of the row at `rowIndex`. */
    onSetRowFusion: (rowIndex: number, fusion: RowFusion | undefined) => void;
    /** Commit a layout change (resize). Receives an updater for the latest state. */
    onLayoutChange: (update: (previous: Layout) => Layout) => void;
    /**
     * The series a named window preset chosen on one tile fans out to;
     * a typed value or a step writes the tile's own series alone.
     */
    windowLevelTargets: WindowLevelFanout;
    /** The dataset binding controller: bind a dataset into an empty row. */
    binding: DatasetBinding;
}

// Roving-tabindex arrow navigation: each key steps one cell in the grid.
const ARROW_STEP: Record<string, { row: number; column: number }> = {
    ArrowRight: { row: 0, column: 1 },
    ArrowLeft: { row: 0, column: -1 },
    ArrowDown: { row: 1, column: 0 },
    ArrowUp: { row: -1, column: 0 },
};

interface ResolvedCell {
    rowIndex: number;
    plane: Plane;
    /** Which of the row's fused pair the cell draws. */
    layer: ImageLayer;
    id: ViewportId;
    label: string | undefined;
    volume: RenderableVolume | undefined;
}

/** A plane pane with its row's volume resolved, or `undefined` for any other id. */
function findCell(
    layout: Layout,
    datasetAdapter: DatasetAdapter,
    id: ViewportId
): ResolvedCell | undefined {
    const located = locateViewport(layout, id);
    if (!located) {
        return undefined;
    }
    const { rowIndex, row, cell } = located;
    const { content } = cell;
    if (content.kind !== 'plane') {
        return undefined;
    }
    const volume = row.dataset
        ? datasetAdapter.resolve(row.dataset.seriesInstanceUid)
        : undefined;
    return {
        rowIndex,
        plane: content.plane,
        layer: content.layer,
        id: cell.id,
        label: volume?.metadata.label,
        volume,
    };
}

interface MaximizedTileProps {
    volume: RenderableVolume;
    plane: Plane;
    layer: ImageLayer;
    /** Present when the tile's row is locked to the primary row. */
    alignment: TileAlignment | undefined;
    /** Present when the tile's row is independent but registered to the primary row. */
    structuresThrough: TileAlignment | undefined;
    /** The tile's part in keeping tiles of its image at one scale and centre. */
    geometryLink: TileGeometryLink | undefined;
    /** The row's blend, so a secondary pane knows whether it has one. */
    blend: RowBlendControls;
    /** The images the grid offers, for the title's series numbers. */
    listedImages: ReadonlyArray<ListedImage>;
    /** The row's plane control, for the plane this tile shows next. */
    planeCycle: PlaneCycleControl;
    scaleBarVisible: boolean;
    /** The window/level of the images the tile draws, for their histograms. */
    windowing: WindowingAdapter;
    renderer: RenderAdapter;
    /** The row's oblique frame and the way back from it. */
    frame: RowFrameControls;
    onSelectRoi: RoiSelectionHandler | undefined;
    structureActions: TileStructureActions | undefined;
    /** The tile's canvas overlay, for the images the tile leads. */
    overlayFor: (ledImages: ReadonlyArray<SeriesInstanceUid>) => ReactNode;
    statusFor: (context: TileStatusContext) => ReactNode;
    imageDetails: ((context: ImageDetailsContext) => ReactNode) | undefined;
    onMaximize: () => void;
}

/**
 * The one tile shown while maximized, labelled by the image it draws. It is
 * alone on screen, so it always names that image (the Tile default), and it
 * carries its row's plane control.
 */
function MaximizedTile({
    volume,
    plane,
    layer: rowLayer,
    alignment,
    structuresThrough,
    geometryLink,
    blend,
    planeCycle,
    scaleBarVisible,
    windowing,
    renderer,
    frame,
    onSelectRoi,
    structureActions,
    overlayFor,
    statusFor,
    imageDetails,
    onMaximize,
    listedImages,
}: MaximizedTileProps) {
    const blendSource = blend.source;
    useChangeVersion(blendSource);
    const intl = useIntl();
    // A locked row shows its own image alone, whatever its pair.
    const layer = alignment ? ImageLayer.Primary : rowLayer;
    const fused = alignment ? undefined : blendSource.getCurrent();
    const shownImage = shownImageOfPane(layer, volume.metadata, fused);
    const drawnImages = imagesDrawnByPane(
        layer,
        volume.metadata,
        fused,
        (seriesNumber) => seriesNumberText(intl, seriesNumber)
    );
    if (!shownImage) {
        return (
            <SecondaryImagePlaceholder
                source={blendSource}
                onFuse={blend.onFuse}
                onClear={blend.onClear}
            />
        );
    }
    return (
        <Tile
            volume={volume}
            plane={plane}
            layer={layer}
            alignment={alignment}
            structuresThrough={structuresThrough}
            geometryLink={geometryLink}
            fusion={tileFusionOf(blend)}
            shownImage={shownImage}
            fusedImage={fusedImageNamedByPane(layer, fused)}
            listedImages={listedImages}
            renderer={renderer}
            frame={frame}
            isActive
            isMaximized
            onSelectRoi={onSelectRoi}
            structureActions={structureActions}
            overlay={overlayFor(
                imagesLedByPane(layer, [], volume.metadata, fused)
            )}
            status={statusFor({ images: drawnImages, leadsRow: true })}
            imageDetails={imageDetails}
            drawnImages={drawnImages}
            planeCycle={planeCycle}
            scaleBarVisible={scaleBarVisible}
            windowHistograms={resolvePaneHistograms(
                drawnImages,
                volume.metadata,
                fused,
                blendSource.getBlend(),
                windowing
            )}
            onMaximize={onMaximize}
        />
    );
}

/**
 * Renders the layout as a stack of resizable dataset rows, wired to the grid
 * actions. Each viewport keeps a stable RenderAdapter keyed by its
 * ViewportId, so resizing (pure CSS) never remounts a tile's canvas. A row
 * and a column group are keyed by their first tile, so a drag-rearrange
 * that changes the first tile of a row or of a column group remounts the
 * tiles of that row or group. Drag the title bar to reorder within a row;
 * cross-row drops are rejected (a row never mixes datasets). The engine
 * owns slice/cursor/window-level truth.
 */
function ViewportGridInner({
    layout,
    datasetAdapter,
    environmentAdapter,
    windowingAdapter,
    blendAdapter,
    activeId,
    unboundRowActive = false,
    maximizedId,
    scaleBarVisible = false,
    tileOverlay,
    tileStatus,
    imageDetails,
    rowOverlayControls,
    panels = [],
    widgets = [],
    editableSeriesUid,
    primaryRowNotice,
    primaryImage,
    onActivate,
    onActivateUnboundRow,
    onActiveRendererChanged,
    canSelectRoi = false,
    onSelectRoi,
    structureActions,
    onToggleMaximize,
    onAddRowBelow,
    onCloseRow,
    onReorder,
    onToggleSideBySide,
    onRotateRowPlanes,
    onRehangRow,
    onSetRowLock,
    onSetRowFusion,
    onLayoutChange,
    windowLevelTargets,
    binding,
}: ViewportGridProps) {
    const intl = useIntl();
    const renderers = useRef(new Map<ViewportId, RenderAdapter>());
    // A renderer belongs to the adapter that minted it. When the adapter
    // changes, the tiles must mount renderers of the new one in this very
    // render, so the pool is swapped here and the old one is released by
    // the cleanup below, which still holds it with its own adapter.
    const poolAdapter = useRef(environmentAdapter);
    if (poolAdapter.current !== environmentAdapter) {
        poolAdapter.current = environmentAdapter;
        renderers.current = new Map();
    }
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
    );
    const [activeDragId, setActiveDragId] = useState<ViewportId | undefined>(
        undefined
    );

    const rendererFor = (id: ViewportId): RenderAdapter => {
        const existing = renderers.current.get(id);
        if (existing) {
            return existing;
        }
        const created = environmentAdapter.createTileRenderer(id);
        renderers.current.set(id, created);
        return created;
    };

    const overlayFor = (
        renderer: RenderAdapter,
        ledImages: ReadonlyArray<SeriesInstanceUid>
    ): ReactNode => tileOverlay?.({ renderer, ledImages });
    const statusFor = (context: TileStatusContext): ReactNode =>
        tileStatus?.(context);
    const panelFor = (kind: PanelKind): GridPanel | undefined =>
        panels.find((panel) => panel.kind === kind);
    // Kept while the grid lives: a widget reopened comes back where it was.
    const [widgetFrames, setWidgetFrames] = useState<
        ReadonlyMap<string, WidgetFrame>
    >(() => new Map());
    const setWidgetFrame = useCallback(
        (id: string, frame: WidgetFrame) =>
            setWidgetFrames((current) => new Map(current).set(id, frame)),
        []
    );
    const widgetLayerFor = (rowIndex: number): ReactNode => {
        const hosted = widgets.filter(
            (widget) =>
                (widget.hostSeriesUid === undefined
                    ? PRIMARY_ROW_INDEX
                    : rowIndexOfSeries(layout, widget.hostSeriesUid)) ===
                rowIndex
        );
        return hosted.length > 0 ? (
            <RowWidgetLayer
                widgets={hosted}
                frames={widgetFrames}
                onFrameChange={setWidgetFrame}
            />
        ) : null;
    };
    /** The panel a pane shows, or `undefined` for an image pane or an unknown id. */
    const panelOfPane = (id: ViewportId): GridPanel | undefined => {
        const cell = locateViewport(layout, id)?.cell;
        const kind = cell ? panelKindOfCell(cell) : undefined;
        return kind === undefined ? undefined : panelFor(kind);
    };
    // Every other row locks to the primary row's image, through the bindings
    // that image offers, so a change to those reaches every row's lock.
    const primarySeriesUid =
        layout.rows[PRIMARY_ROW_INDEX]?.dataset?.seriesInstanceUid;
    const primaryBlendSource = primarySeriesUid
        ? blendAdapter.sourceFor(primarySeriesUid)
        : undefined;
    const subscribePrimaryBlend = useCallback(
        (listener: () => void) =>
            primaryBlendSource?.subscribe(listener) ?? (() => undefined),
        [primaryBlendSource]
    );
    const primaryBlendVersion = useSyncExternalStore(
        subscribePrimaryBlend,
        () => primaryBlendSource?.getVersion() ?? 0
    );
    const rowLockFor = (rowIndex: number): RowLockState => {
        const row = layout.rows[rowIndex];
        return row
            ? rowLockStateOf(
                  row,
                  rowIndex,
                  primarySeriesUid,
                  primaryBlendSource?.getCandidates() ?? []
              )
            : { kind: 'none' };
    };
    // A row locked by default stores the registration it was locked
    // through, so a registration approved, imported or deleted later never
    // moves it to another one unasked.
    useEffect(() => {
        const candidates = primaryBlendSource?.getCandidates() ?? [];
        layout.rows.forEach((row, rowIndex) => {
            if (row.lock !== undefined) {
                return;
            }
            const state = rowLockStateOf(
                row,
                rowIndex,
                primarySeriesUid,
                candidates
            );
            if (state.kind === 'locked') {
                onSetRowLock(rowIndex, {
                    ...lockThrough(state.current, state.mode),
                    byDefault: true,
                });
            }
        });
    }, [
        layout.rows,
        primarySeriesUid,
        primaryBlendSource,
        primaryBlendVersion,
        onSetRowLock,
    ]);
    // The primary row draws the fusion its image's Environment holds, the
    // sidebar's pair; every other row holds its own, so two rows of one
    // image fuse independently.
    const rowBlendFor = (rowIndex: number): RowBlendControls | undefined => {
        const row = layout.rows[rowIndex];
        const volume = row?.dataset
            ? datasetAdapter.resolve(row.dataset.seriesInstanceUid)
            : undefined;
        if (!row || !volume) {
            return undefined;
        }
        return rowIndex === PRIMARY_ROW_INDEX
            ? resolveRowBlend(volume.metadata.seriesInstanceUid, blendAdapter)
            : resolveOwnRowBlend(
                  volume.metadata,
                  row.fusion,
                  blendAdapter,
                  (fusion) => onSetRowFusion(rowIndex, fusion)
              );
    };
    const selectionFor = (volume: RenderableVolume) =>
        roiSelectionFor(volume, editableSeriesUid, canSelectRoi, onSelectRoi);
    const actionsFor = (volume: RenderableVolume) =>
        structureActionsFor(volume, editableSeriesUid, structureActions);

    const gridRef = useRef<HTMLDivElement>(null);
    const firstTileId = layout.rows.find((row) => row.cells.length > 0)
        ?.cells[0]?.id;
    // Roving tabindex: the active tile (or the first, before any activation)
    // is the single Tab stop; arrow keys move between tiles by address.
    const rovingTabTarget = activeId ?? firstTileId;

    // An arrow the engine already used (a slice scroll under the pointer)
    // does not also move focus, an arrow with a modifier belongs to other
    // shortcuts (Ctrl+Arrow nudges a registration), and an arrow in a text
    // field or a slider (a footer value, the blend share) belongs to that
    // control. An arrow pressed in a menu, or in anything the grid's DOM
    // does not hold (a footer menu opens in a portal, and its keys still
    // reach here through React), is left to that control: the pane behind
    // it stays active.
    const handleGridKeyDown = (event: ReactKeyboardEvent) => {
        const step = ARROW_STEP[event.key];
        const keyTarget = event.target;
        if (
            !step ||
            !rovingTabTarget ||
            event.defaultPrevented ||
            !(keyTarget instanceof Element) ||
            !gridRef.current?.contains(keyTarget) ||
            keyTarget.closest('[role="menu"]') !== null ||
            isExcludedFromViewInput(keyTarget) ||
            isTextEntryElement(keyTarget) ||
            event.ctrlKey ||
            event.metaKey ||
            event.altKey ||
            event.shiftKey
        ) {
            return;
        }
        const target = neighbourViewport(
            layout,
            rovingTabTarget,
            step.row,
            step.column
        );
        if (!target) {
            return;
        }
        event.preventDefault();
        onActivate(target.id);
        gridRef.current
            ?.querySelector<HTMLElement>(`[data-tile-id="${target.id}"]`)
            ?.focus();
    };

    const viewportCount = layout.rows.reduce(
        (total, row) => total + row.cells.length,
        0
    );
    // The console never falls to an empty grid: the last row stays.
    const canCloseRow = layout.rows.length > 1;

    useEffect(() => {
        const live = new Set<ViewportId>();
        layout.rows.forEach((row) =>
            row.cells.forEach((cell) => live.add(cell.id))
        );
        const pool = renderers.current;
        pool.forEach((renderer, id) => {
            if (!live.has(id)) {
                environmentAdapter.releaseTileRenderer(renderer);
                pool.delete(id);
            }
        });
    }, [layout, environmentAdapter]);

    // Releases the pool of this adapter when the adapter changes or the
    // grid unmounts. The closure holds the pool the adapter minted, so an
    // adapter change releases the old pool with the old adapter.
    useEffect(() => {
        const pool = renderers.current;
        return () => {
            pool.forEach((renderer) =>
                environmentAdapter.releaseTileRenderer(renderer)
            );
            pool.clear();
        };
    }, [environmentAdapter]);

    // Whether the active pane draws an image, by the same test a pane uses
    // to choose between its tile and its placeholder. A secondary pane
    // draws one only while its row is fused, so the row's blend source is
    // followed for as long as the pane is active.
    const activeCell = activeId
        ? findCell(layout, datasetAdapter, activeId)
        : undefined;
    const activeVolume = activeCell?.volume;
    const activeBlendSource = activeCell
        ? rowBlendFor(activeCell.rowIndex)?.source
        : undefined;
    const subscribeActiveBlend = useCallback(
        (listener: () => void) =>
            activeBlendSource?.subscribe(listener) ?? (() => undefined),
        [activeBlendSource]
    );
    // A pane of a locked row draws its row's image alone, whatever its pair.
    const activeRowLocked =
        activeCell !== undefined &&
        rowLockFor(activeCell.rowIndex).kind === 'locked';
    const activeShowsImage = useSyncExternalStore(
        subscribeActiveBlend,
        () =>
            activeCell !== undefined &&
            activeVolume !== undefined &&
            (activeRowLocked ||
                shownImageOfPane(
                    activeCell.layer,
                    activeVolume.metadata,
                    activeBlendSource?.getCurrent()
                ) !== undefined)
    );

    // The renderer of an id is stable while its cell lives and its adapter
    // is the same, and the host clears an id whose cell is gone before this
    // runs, so the active id, whether its pane draws an image and the
    // adapter key the publication. After an adapter change the pool already
    // holds the new renderer, minted during the render; after a fuse the
    // pane's tile has mounted its renderer before this runs. The cleanup
    // withdraws the handle, so a change and the unmount both leave no stale
    // handle behind.
    useEffect(() => {
        if (!onActiveRendererChanged) {
            return undefined;
        }
        onActiveRendererChanged(
            activeId !== undefined && activeShowsImage
                ? renderers.current.get(activeId)
                : undefined,
            activeId !== undefined || unboundRowActive
        );
        return () => onActiveRendererChanged(undefined, false);
    }, [
        activeId,
        unboundRowActive,
        activeShowsImage,
        environmentAdapter,
        onActiveRendererChanged,
    ]);

    useEffect(() => {
        if (!maximizedId) {
            return undefined;
        }
        // An Escape something else already handled, such as a tool
        // cancelling its action in progress, leaves the tile maximized.
        const onKey = (event: KeyboardEvent) => {
            if (event.key !== 'Escape' || event.defaultPrevented) {
                return;
            }
            onToggleMaximize(maximizedId);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [maximizedId, onToggleMaximize]);

    const planeCycleFor = (
        id: ViewportId,
        rowIndex: number
    ): PlaneCycleControl => ({
        nextPlane: nextPlaneOfPane(layout, id),
        onCycle: () => onRotateRowPlanes(rowIndex),
    });

    const activeDrag = activeDragId
        ? locateViewport(layout, activeDragId)
        : undefined;
    // What the drag overlay names the dragged pane by: a panel by its
    // title, an image pane by its image (masked as PHI) and its plane.
    const activeDragPanel = activeDragId
        ? panelOfPane(activeDragId)
        : undefined;
    const activeDragImage =
        activeDragId && !activeDragPanel
            ? findCell(layout, datasetAdapter, activeDragId)
            : undefined;

    const host: RowHost = {
        datasetAdapter,
        windowingAdapter,
        windowLevelTargets,
        binding,
        environmentAdapter,
        rendererFor,
        overlayFor,
        statusFor,
        imageDetails,
        rowOverlayControls,
        activeId,
        scaleBarVisible,
        planeCycleFor,
        panelFor,
        widgetLayerFor,
        dragRowIndex: activeDrag?.rowIndex,
        dragIsPanel: activeDrag ? isPanelCell(activeDrag.cell) : undefined,
        activeDragId,
        rovingTabTarget,
        canCloseRow,
        primaryRowNotice,
        primaryImage,
        primarySeriesUid,
        rowLockFor,
        onSetRowLock,
        rowBlendFor,
        roiSelectionFor: selectionFor,
        structureActionsFor: actionsFor,
        onActivate,
        onActivateUnboundRow,
        onToggleMaximize,
        onAddRowBelow,
        onCloseRow,
        onToggleSideBySide,
        onRehangRow,
        onLayoutChange,
    };

    /** The one tile shown while maximized: a panel, or an image pane. */
    const renderMaximized = (id: ViewportId): ReactNode => {
        const panel = panelOfPane(id);
        if (panel) {
            return (
                <PanelTile
                    title={panel.title}
                    subject={panel.subject}
                    actions={panel.actions}
                    isActive
                    isMaximized
                    onMaximize={() => onToggleMaximize(id)}
                >
                    {panel.body({ maximized: true })}
                </PanelTile>
            );
        }
        const image = findCell(layout, datasetAdapter, id);
        if (!image?.volume) {
            return undefined;
        }
        const renderer = rendererFor(id);
        const blend = rowBlendFor(image.rowIndex);
        if (!blend) {
            return undefined;
        }
        const lockState = rowLockFor(image.rowIndex);
        const alignment = tileAlignmentOf(lockState, primarySeriesUid);
        // A locked tile is a view of the primary image: its frame and its
        // structures are the primary's.
        const viewedVolume =
            (alignment && datasetAdapter.resolve(alignment.anchorSeriesUid)) ||
            image.volume;
        return (
            <MaximizedTile
                volume={image.volume}
                plane={image.plane}
                layer={image.layer}
                alignment={alignment}
                structuresThrough={tileStructureSourceOf(
                    lockState,
                    primarySeriesUid
                )}
                geometryLink={tileGeometryLinkOf(lockState, image.rowIndex)}
                blend={blend}
                listedImages={binding.availableDatasets}
                planeCycle={planeCycleFor(id, image.rowIndex)}
                scaleBarVisible={scaleBarVisible}
                windowing={windowingAdapter}
                renderer={renderer}
                frame={resolveRowFrame(
                    viewedVolume.metadata.seriesInstanceUid,
                    environmentAdapter
                )}
                onSelectRoi={selectionFor(viewedVolume)}
                structureActions={actionsFor(viewedVolume)}
                overlayFor={(ledImages) => overlayFor(renderer, ledImages)}
                statusFor={statusFor}
                imageDetails={imageDetails}
                onMaximize={() => onToggleMaximize(id)}
            />
        );
    };

    const maximized = maximizedId ? renderMaximized(maximizedId) : undefined;
    if (maximizedId && maximized) {
        // A maximized pane of the primary row keeps the row's notice, so it
        // never shows an image apart from the sidebar with nothing said. An
        // image pane keeps its row's footer, so the window a drag on it
        // changes stays on screen with its values, presets and histogram.
        const located = locateViewport(layout, maximizedId);
        // A footer action that changes the row itself (its image, shape or
        // place in the stack) restores the grid first, so the change is seen
        // on the whole row and its neighbours. Window and blend changes stay
        // on the maximized tile, which shows them: a named preset reaches the
        // maximized row's own series alone, never a series of a row the
        // maximize hides.
        const maximizedSeriesUid = located?.row.dataset?.seriesInstanceUid;
        const restoreThen =
            <Args extends unknown[]>(action: (...args: Args) => void) =>
            (...args: Args) => {
                onToggleMaximize(maximizedId);
                action(...args);
            };
        const maximizedHost: RowHost = {
            ...host,
            windowLevelTargets: (id) =>
                windowLevelTargets(id).filter(
                    (uid) => uid === maximizedSeriesUid
                ),
            binding: {
                ...binding,
                requestBind: restoreThen(binding.requestBind),
            },
            onAddRowBelow: restoreThen(onAddRowBelow),
            onCloseRow: restoreThen(onCloseRow),
            onToggleSideBySide: restoreThen(onToggleSideBySide),
            onRehangRow: restoreThen(onRehangRow),
            primaryImage: {
                ...host.primaryImage,
                choose: restoreThen(host.primaryImage.choose),
            },
        };
        const notice =
            located?.rowIndex === PRIMARY_ROW_INDEX
                ? primaryRowNotice
                : undefined;
        return (
            <Box
                data-testid="maximized-tile"
                style={LEFT_EDGE_PANE_STYLE}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                {notice ? <PrimaryRowNotice text={notice} /> : null}
                <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                    {maximized}
                    {located ? widgetLayerFor(located.rowIndex) : null}
                </Box>
                {located && !isPanelCell(located.cell) ? (
                    <GridRowFooter
                        row={located.row}
                        rowIndex={located.rowIndex}
                        host={maximizedHost}
                    />
                ) : null}
            </Box>
        );
    }

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveDragId(undefined);
        const draggedId = event.active.id as ViewportId;
        const overId = event.over?.id as ViewportId | undefined;
        if (!overId || overId === draggedId) {
            return;
        }
        const dragged = locateViewport(layout, draggedId);
        const target = locateViewport(layout, overId);
        if (!dragged || !target || dragged.rowIndex !== target.rowIndex) {
            return; // same-row reorder only — datasets stay on their row
        }
        const targetColumnIndex = target.row.cells.findIndex(
            (cell) => cell.id === overId
        );
        onReorder(draggedId, targetColumnIndex);
    };

    return (
        <DndContext
            sensors={sensors}
            onDragStart={(event) =>
                setActiveDragId(event.active.id as ViewportId)
            }
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveDragId(undefined)}
        >
            <Box
                ref={gridRef}
                role="group"
                aria-label={intl.formatMessage({ id: 'viewportGrid.name' })}
                onKeyDown={handleGridKeyDown}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                <Box
                    data-testid="grid-live-region"
                    role="status"
                    aria-live="polite"
                    sx={visuallyHidden}
                >
                    {intl.formatMessage(
                        { id: 'viewportGrid.liveRegion' },
                        { viewports: viewportCount, rows: layout.rows.length }
                    )}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <PanelGroup
                        direction="vertical"
                        onLayout={(sizes) =>
                            onLayoutChange((previous) =>
                                setRowHeights(previous, sizes)
                            )
                        }
                        style={{ height: '100%' }}
                    >
                        {layout.rows.map((row, rowIndex) => {
                            const rowKey =
                                row.cells[0]?.id ?? `row-${rowIndex}`;
                            return (
                                <Fragment key={rowKey}>
                                    {rowIndex > 0 ? (
                                        <ResizeGutter direction="vertical" />
                                    ) : null}
                                    <Panel
                                        id={rowKey}
                                        defaultSize={row.heightPercent}
                                        minSize={8}
                                        order={rowIndex}
                                    >
                                        <GridRow
                                            row={row}
                                            rowIndex={rowIndex}
                                            host={host}
                                        />
                                    </Panel>
                                </Fragment>
                            );
                        })}
                    </PanelGroup>
                </Box>
            </Box>
            <DragOverlay>
                {activeDragPanel || activeDragImage?.label ? (
                    <Box
                        sx={{
                            px: 1,
                            py: 0.5,
                            bgcolor: CHROME_BG,
                            border: '1px solid',
                            borderColor: 'primary.main',
                            color: '#fff',
                            fontFamily: READOUT_FONT,
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                            cursor: 'grabbing',
                        }}
                    >
                        {activeDragPanel ? (
                            activeDragPanel.title
                        ) : activeDragImage ? (
                            <>
                                <SensitiveInfoTypography>
                                    {activeDragImage.label}
                                </SensitiveInfoTypography>{' '}
                                / {planeLabel(intl, activeDragImage.plane)}
                            </>
                        ) : null}
                    </Box>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}

/**
 * The imaging-console chrome is always dark (radiology convention), independent
 * of the app's light/dark mode — so the grid provides its own dark theme to the
 * whole subtree. Tile chrome wires to these theme tokens.
 */
function ViewportGrid(props: ViewportGridProps) {
    return (
        <ThemeProvider theme={chromeTheme}>
            <ViewportGridInner {...props} />
        </ThemeProvider>
    );
}

export default ViewportGrid;
